import { Box, Skeleton, useTheme } from "@mui/material";
import { BarDatum } from "@nivo/bar";
import { ResponsiveBar } from "@common/components/nivoCharts/NivoCharts";
import { DotLegalEmptyState } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { riskChartTheme, riskChartTooltipContainerSx } from "../../riskChartStyles";
import { getRiskSettingsIcon } from "../../../riskSettings/common/RiskSettings.utils";
import { RisksOverAppetiteByAreaItem } from "../RiskManagementReport.types";
import { buildCountAxisTicks } from "../RiskManagementReport.utils";
import { chartContentHeight } from "../RiskReportChartPanel";

interface RisksOverAppetiteByAreaChartProps {
    isLoading: boolean;
    areas: Array<RisksOverAppetiteByAreaItem>;
}

// The left axis gutter is sized to the longest label rather than fixed, so short names don't leave a large
// empty strip before their bar, and it is capped so a long name can't crowd out the bars. Label width is
// estimated from its character count at fontSize 13 plus the gap between the label and the plot. The
// truncation length is derived from the cap so a label that would overflow the capped gutter is shortened
// with an ellipsis instead of being clipped; the full name stays available on hover.
const approximateAreaLabelCharWidth = 7.5;
const areaLabelGutter = 24;
const maxAreaAxisWidth = 150;
const maxAreaLabelLength = Math.floor((maxAreaAxisWidth - areaLabelGutter) / approximateAreaLabelCharWidth);

// Each bar gets at least this much vertical room. When the areas outgrow the fixed panel height the chart
// grows past it and the panel scrolls, rather than squeezing every bar thinner.
const minBarRowHeight = 40;

function RisksOverAppetiteByAreaChart({ isLoading, areas }: RisksOverAppetiteByAreaChartProps) {
    const theme = useTheme();
    const { translateString } = useTranslation();

    // Darkest shade is the highest-ranked area, fading to lighter shades for lower ranks. The two mid shades
    // have no theme token, so they stay as literals.
    const riskAreaPalette = [theme.palette.primary.dark, theme.palette.primary.main, "#F3BAED", "#D9BCFF", theme.palette.secondary.main];

    if (isLoading) {
        return <Skeleton variant="rounded" width="100%" height="100%" />;
    }

    if (areas.length === 0) {
        return (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <DotLegalEmptyState noFixedHeight icon={getRiskSettingsIcon()} text={translateString("noData")} />
            </Box>
        );
    }

    const rankedDescending = [...areas].sort((a, b) => b.value - a.value);
    // Nivo renders the first horizontal bar at the bottom, so reverse the descending order to put the highest on top.
    const chartData: Array<BarDatum> = rankedDescending.map((entry, rank) => ({ ...entry, color: riskAreaPalette[rank % riskAreaPalette.length] })).reverse();

    const highestValue = Math.max(1, ...areas.map((area) => area.value));
    const { axisMax, tickValues } = buildCountAxisTicks(highestValue);

    const longestLabelLength = Math.min(maxAreaLabelLength, Math.max(0, ...areas.map((area) => area.area.length)));
    const leftMargin = Math.min(maxAreaAxisWidth, Math.round(longestLabelLength * approximateAreaLabelCharWidth) + areaLabelGutter);

    // Below the threshold the chart fills the panel exactly (overflow hidden so no spurious scrollbar); above it
    // the chart grows to keep each bar readable and the panel scrolls vertically.
    const fullChartHeight = chartData.length * minBarRowHeight;
    const needsScroll = fullChartHeight > chartContentHeight;

    const renderAreaTick = ({ value, x, y }: { value: string | number; x: number; y: number }) => {
        const label = String(value);
        const isTruncated = label.length > maxAreaLabelLength;
        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    textAnchor="end"
                    dominantBaseline="central"
                    transform="translate(-12,0)"
                    style={{ fill: theme.palette.primary.dark, fontSize: 13, fontFamily: theme.typography.fontFamily }}
                >
                    {isTruncated && <title>{label}</title>}
                    {isTruncated ? `${label.slice(0, maxAreaLabelLength - 1)}…` : label}
                </text>
            </g>
        );
    };

    return (
        <Box sx={{ height: "100%", width: "100%", overflowY: needsScroll ? "auto" : "hidden", overflowX: "hidden" }}>
            <Box sx={{ height: needsScroll ? fullChartHeight : "100%", "& svg g rect": { cursor: "pointer" } }}>
                <ResponsiveBar
                    data={chartData}
                    layout="horizontal"
                    indexBy="area"
                    keys={["value"]}
                    colors={(bar) => bar.data.color as string}
                    maxValue={axisMax}
                    margin={{ top: 10, right: 20, bottom: 30, left: leftMargin }}
                    padding={0.4}
                    borderRadius={4}
                    enableLabel={false}
                    enableGridX
                    gridXValues={tickValues}
                    enableGridY={false}
                    isInteractive
                    tooltip={({ value }) => <Box sx={{ ...riskChartTooltipContainerSx(theme), padding: "4px 8px", fontWeight: 600 }}>{value}</Box>}
                    axisBottom={{ tickValues, tickSize: 0, tickPadding: 8 }}
                    axisLeft={{ tickSize: 0, tickPadding: 12, renderTick: renderAreaTick }}
                    theme={riskChartTheme(theme)}
                />
            </Box>
        </Box>
    );
}

export default RisksOverAppetiteByAreaChart;
